<script setup>
import {inject, reactive, ref, watch} from "vue";
import needsService from "@/services/ecosystem/needs.service";
import TablePager from "@/components/TablePager.vue";
import useGrid from "@/composables/useGrid";
import {getLogo} from "@/helpers/logo";
import {useSingleRowItems} from "@/composables/useSingleRowItems";

const emit = defineEmits(["submitStartup"]);
const {gridParams, gridMethods, items} = useGrid(getStartups, 5);
const {rowRef} = useSingleRowItems();

const need = inject('need');
const updateStartups = inject('updateStartups');

const filters = reactive({
    search: '',
    category: '',
});

const categories = ref([]);

watch(filters, () => {
    gridMethods.updateList();
}, {immediate: true});

watch(() => filters.search, () => {
    getCategories();
}, {immediate: true});

watch(updateStartups, (value) => {
    if (value) {
        gridMethods.updateList();
    }
});

function applySearch(search) {
    filters.search = search;
}

async function getCategories() {
    const {data} = await needsService.getRelevantStartupsCategories(need.value.id, filters.search);

    categories.value = data;
}

async function getStartups(pageParams) {
    pageParams.filters = filters;

    return needsService.getAllStartups(need.value.id, pageParams);
}
</script>

<template>
    <div class="dashboard-page__card need-admin__card--double">
        <div class="dashboard-page__card__inner">
            <header class="dashboard-page__card__header">
                <h3 class="dashboard-page__title dashboard-page__title--startups">All startups</h3>
                <div class="ml-auto">
                    <input v-debounce:500ms="applySearch" type="text" placeholder="Search for a startup" class="dashboard-page__search">
                </div>
            </header>

            <div class="dashboard-page__filter--scroll dashboard-page__filter--scroll--secondary">
                <span
                    class="filter-item filter-item--all"
                    :class="{active: !filters.category}"
                    @click="filters.category = ''"
                >
                    All Startups
                </span>

                <span
                    v-for="category in categories"
                    :key="category.id"
                    class="filter-item"
                    :class="{active: filters.category === category.id}"
                    :title="category.startups_count"
                    @click="filters.category = category.id"
                >
                    {{category.name}}
                </span>
            </div>

            <div class="dashboard-page__table dashboard-page__table-flexible">
                <table>
                    <tbody>
                    <tr v-for="startup in items" :key="startup.id">
                        <td class="pl-0">
                            <div class="dashboard-page__table__title">
                                <div class="data-labels-set data-labels-set--lg">
                                    <span class="data-label">
                                        <img :src="getLogo(startup.logo)"/>
                                    </span>
                                </div>

                                <div>
                                    <h6 class="heading6"><a :href="`/startups/${startup.id}`" target="_blank">{{ startup.name }}</a></h6>
                                    <p v-if="startup.score">{{ startup.score }}% match</p>
                                </div>
                            </div>
                        </td>
                        <td>
                            <div
                                v-if="startup.categories.length"
                                class="status-plate-wrapper"
                                :ref="el => rowRef(el, startup.id)"
                            >
                                <span
                                    v-for="category in startup.categories"
                                    :key="category"
                                    data-row-item
                                    class="status-plate status-plate--unreviewed"
                                >
                                    {{ category }}
                                </span>
                            </div>
                        </td>
                        <td align="right" class="pr-0">
                            <button v-if="startup.submitted" disabled class="button button--lg button-secondary">
                                Startup Submitted
                            </button>

                            <button
                                v-else
                                class="button button--lg button-primary"
                                @click="emit('submitStartup', startup.id)"
                            >
                                Submit Startup
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <TablePager
                v-if="items.length"
                :pages-count="gridParams.pagesCount"
                :init-page="gridParams.currentPage"
                @changePage="gridMethods.changePage"
            />
        </div>
    </div>
</template>

<style scoped>

</style>
